/* eslint-disable sonarjs/no-duplicate-string */
import { DateTime } from "luxon";
import { v4 } from "uuid";
import { CUSTOM_LINK, CUSTOM_LINK_KEY, DATE_KEY, E_TICKET_KEY, FORM_LINK_BUTTON_KEY, LOCATION_KEY, MAGICAL_LINK_KEY, PREFILLED_FORM_ID, PREFILLED_FORM_KEY, VARIABLE_TEXT, PREFILLED_FORM_TYPE, VARIABLE_DATA_ATTR, BUTTON_COLOR, END_DATE_KEY, } from "../constants/wysiwyg";
import { FormType } from "../enums";
import { StaticVariableFormatKey, StaticVariableTag } from "../enums/wysiwyg";
import { formatDateTimeToLocaleString } from "./dates";
import { getDonationFormMagicalLink, getTicketingMagicalLink, } from "./magicalLink";
export const dynamicVariableTag = {
    getTag: (key) => `{{${key}}}`,
};
export const knownVariables = [
    { tag: StaticVariableTag.FirstName, formatKey: StaticVariableFormatKey.FirstName },
    { tag: StaticVariableTag.LastName, formatKey: StaticVariableFormatKey.LastName },
];
export const parseWysiwygCustomVariables = (wysiwygVariables) => {
    return {
        linkVariables: Object.values(wysiwygVariables?.linkVariables || {}),
    };
};
export const getVariableHtml = ({ text, tag, extraProps, interactive, }) => `<span class="fr-deletable styled-tag-chip"${interactive ? ' style="cursor: pointer;"' : ""} contenteditable="false" ${VARIABLE_DATA_ATTR}="${tag}"${extraProps ? " " + extraProps : ""}>${text}</span>`;
export const getButtonHtml = ({ text, tag, interactive }) => `<span class="fr-deletable styled-tag-button"${interactive ? ' style="cursor: pointer;"' : ""} contenteditable="false" ${VARIABLE_DATA_ATTR}="${tag}" >${text}</span>`;
export const getPrefilledformVariable = (text, tag, { formId, formType }) => {
    const formIdVariable = `${PREFILLED_FORM_ID}="${dynamicVariableTag.getTag(formId)}"`;
    const formTypeVariable = `${PREFILLED_FORM_TYPE}="${dynamicVariableTag.getTag(formType)}"`;
    const cursorPointerStyle = 'style="cursor: pointer;"';
    return getVariableHtml({
        text,
        tag,
        extraProps: `${formIdVariable} ${formTypeVariable} ${cursorPointerStyle}`,
        interactive: true,
    });
};
export const getETicketVariable = (text) => {
    const tag = dynamicVariableTag.getTag(`${E_TICKET_KEY}${v4()}`);
    return getVariableHtml({ text, tag });
};
export const getStartDateVariable = (text, extraProps) => {
    const tag = dynamicVariableTag.getTag(`${DATE_KEY}-${v4()}`);
    return getVariableHtml({ text, tag, extraProps });
};
export const getEndDateVariable = (text, extraProps) => {
    const tag = dynamicVariableTag.getTag(`${END_DATE_KEY}-${v4()}`);
    return getVariableHtml({ text, tag, extraProps });
};
export const getLocationVariable = (text, extraProps) => {
    const tag = dynamicVariableTag.getTag(`${LOCATION_KEY}-${v4()}`);
    return getVariableHtml({ text, tag, extraProps });
};
export const getManyVariableRegex = (keys) => {
    return keys.map((key) => getVariableRegex(key)).join("|");
};
export const getVariableRegex = (key) => `<span[^>]*${VARIABLE_DATA_ATTR}="{{${key}.*?}}"(?:[^>]*fr-original-style="([^"]*)")?[^>]*>(.*?)</span>`;
export const replaceBodyVariables = (body, variable, value) => {
    const regex = getVariableRegex(variable);
    return body.replace(new RegExp(regex, "g"), (_, originalStyle = "") => {
        return originalStyle ? `<span style="${originalStyle}">${value}</span>` : value;
    });
};
const getAnchor = (label, link, index) => 
// style box-sizing is added to make sure the variable has the same behavior style as using react
`<a href="${link}" rel="noopener noreferrer" target="_blank" style="box-sizing: border-box">${label}${index || ""}</a>`;
export const replaceBodyLinkVariables = (body, link, variable) => {
    // For button we keep the entire variable to keep design
    const isButtonVariable = variable.includes(FORM_LINK_BUTTON_KEY) || variable.includes(CUSTOM_LINK_KEY);
    return body.replace(variable, (value) => {
        const label = value.split(">")[1].split("<")[0];
        return getAnchor(isButtonVariable ? value : label, link);
    });
};
export const replaceBodyCustomLinkVariable = (body) => {
    const htmlVariables = getBodyVariables(body, [CUSTOM_LINK_KEY]);
    if (!htmlVariables) {
        return body;
    }
    for (const htmlVariable of htmlVariables) {
        const link = getVariableAttribute(htmlVariable, CUSTOM_LINK);
        if (link) {
            body = replaceBodyLinkVariables(body, link, htmlVariable);
        }
    }
    return body;
};
export const replaceBodyWithToCompleteForms = (frontendUrl, recipient, body, toCompleteForm) => {
    body = replaceBodyWithForm(frontendUrl, body, { regexKey: MAGICAL_LINK_KEY }, toCompleteForm, recipient);
    return body;
};
export const replaceBodyWithSimplesVariables = (defaultBody, recipient) => {
    const { firstName, lastName } = recipient;
    let body = replaceBodyVariables(defaultBody, "firstname", firstName === "N A" ? "" : firstName);
    body = replaceBodyVariables(body, "lastname", lastName === "N A" ? "" : lastName);
    return body;
};
export const replaceBodyLinkVariablesByRegex = (body, link, key) => {
    const regex = new RegExp(getVariableRegex(key), "g");
    return body.replace(regex, (value) => {
        const label = value.split(">")[1].split("<")[0];
        return getAnchor(label, link);
    });
};
export const replaceBodyLinksVariables = (body, links, variable) => {
    return body.replace(variable, (value) => {
        const label = value.split(">")[1].split("<")[0];
        return links.reduce((prev, current, index) => {
            const separator = index === 0 ? "" : " ";
            return prev + separator + getAnchor(label, current, index);
        }, "");
    });
};
export const removeVariableInstancesFromBody = (body, variable) => {
    return body.replace(variable, () => {
        return "";
    });
};
export const getBodyVariables = (body, keys) => {
    const regex = new RegExp(getManyVariableRegex(keys), "g");
    return body.match(regex);
};
export const getFormVariables = async ({ body, variableKeys, }) => {
    const htmlVariables = getBodyVariables(body, variableKeys);
    return htmlVariables?.map((variable) => {
        const key = getVariableAttribute(variable, VARIABLE_DATA_ATTR);
        const formId = getVariableAttribute(variable, PREFILLED_FORM_ID);
        const formType = getVariableAttribute(variable, PREFILLED_FORM_TYPE);
        const text = getVariableAttribute(variable, VARIABLE_TEXT);
        const link = getVariableAttribute(variable, CUSTOM_LINK);
        const buttonColor = getVariableAttribute(variable, BUTTON_COLOR);
        return { key, formId, formType, text, link, buttonColor };
    });
};
export const getVariableAttributeRegex = (parameter) => `${parameter}="{{.*?}}"`;
export const getVariableAttribute = (variable, parameter) => {
    const regex = new RegExp(getVariableAttributeRegex(parameter), "g");
    const attribute = variable.match(regex)?.[0];
    return attribute?.split(`{{`)?.[1]?.split("}}")?.[0];
};
export const replaceBodyWithForm = (frontendUrl, body, params, formObject, recipient) => {
    if (!formObject.form.path) {
        return body;
    }
    const { type, form } = formObject;
    if (type === FormType.DonationForm) {
        const magicalLink = getDonationFormMagicalLink(frontendUrl, recipient, form);
        if (params.regexKey) {
            return replaceBodyLinkVariablesByRegex(body, magicalLink, params.regexKey);
        }
        else if (params.htmlVariable) {
            return replaceBodyLinkVariables(body, magicalLink, params.htmlVariable);
        }
        return body;
    }
    else {
        const magicalLink = getTicketingMagicalLink(frontendUrl, recipient, form.path);
        if (params.regexKey) {
            return replaceBodyLinkVariablesByRegex(body, magicalLink, params.regexKey);
        }
        else if (params.htmlVariable) {
            return replaceBodyLinkVariables(body, magicalLink, params.htmlVariable);
        }
        return body;
    }
};
export const replaceBodyWithPrefilledForms = (frontendUrl, recipient, body, forms) => {
    const htmlVariables = getBodyVariables(body, [PREFILLED_FORM_KEY, FORM_LINK_BUTTON_KEY]);
    if (forms.donationForms || forms.ticketings) {
        (htmlVariables || []).map((htmlVariable) => {
            const formId = getVariableAttribute(htmlVariable, PREFILLED_FORM_ID);
            const formType = getVariableAttribute(htmlVariable, PREFILLED_FORM_TYPE);
            if (formType && formId) {
                const form = formType === FormType.DonationForm ? forms.donationForms[formId] : forms.ticketings[formId];
                if (!form) {
                    return body;
                }
                body = replaceBodyWithForm(frontendUrl, body, { htmlVariable }, { form, type: formType }, recipient);
            }
        });
    }
    return body;
};
export const replaceBodyWithETickets = (eTicketUrls, body, ticketingId) => {
    const htmlVariables = getBodyVariables(body, [E_TICKET_KEY]);
    (htmlVariables || []).map((htmlVariable) => {
        if (eTicketUrls && ticketingId) {
            body = replaceBodyLinksVariables(body, eTicketUrls, htmlVariable);
        }
        else {
            body = removeVariableInstancesFromBody(body, htmlVariable);
        }
    });
    return body;
};
/**
 * Replace Unlayer form link button placeholders with personalized form links
 * Pattern: {{unlayer-form-link-button-FormType_formId}} with optional query parameters
 */
export const replaceUnlayerFormLinkButton = (frontendUrl, recipient, body, prefilledForms) => {
    // Pattern to match: {{unlayer-form-link-button-FormType_formId}} with optional query parameters
    const regex = /{{unlayer-form-link-button-(DonationForm|Ticketing)-([a-zA-Z0-9-]+)(\?[^}]*)?}}/g;
    return body.replace(regex, (match, formType, formId, queryParams) => {
        // Find the form in the prefilled forms
        const form = formType === FormType.DonationForm ? prefilledForms.donationForms?.[formId] : prefilledForms.ticketings?.[formId];
        if (!form) {
            return match; // If form not found, leave the placeholder as is
        }
        // Generate base magical link
        const baseLink = formType === FormType.DonationForm
            ? getDonationFormMagicalLink(frontendUrl, recipient, form)
            : getTicketingMagicalLink(frontendUrl, recipient, form.path);
        // Append query parameters if they exist
        if (queryParams) {
            const params = queryParams.substring(1); // Remove the '?' prefix
            const separator = baseLink.includes("?") ? "&" : "?";
            return `${baseLink}${separator}${params}`;
        }
        return baseLink;
    });
};
export const formatBodyForRecipient = ({ frontendUrl, recipient, body: defaultBody, variables, locale, }) => {
    let body = defaultBody; // Using let allow to be more clear on the mutation cascade of the body
    body = replaceBodyWithSimplesVariables(body, recipient);
    body = replaceBodyCustomLinkVariable(body);
    if (!variables) {
        return body;
    }
    const { toCompleteForms, prefilledForms, ticketingForm } = variables;
    const toCompleteForm = toCompleteForms?.[recipient.id];
    const recipientCommandDetails = ticketingForm?.recipientsWithCommandDetailsMap?.[recipient.id];
    if (prefilledForms) {
        body = replaceBodyWithPrefilledForms(frontendUrl, recipient, body, prefilledForms);
        // Add the Unlayer form link button replacement
        body = replaceUnlayerFormLinkButton(frontendUrl, recipient, body, prefilledForms);
    }
    if (toCompleteForm) {
        body = replaceBodyWithToCompleteForms(frontendUrl, recipient, body, toCompleteForm);
    }
    if (recipientCommandDetails?.eTicketUrls) {
        body = replaceBodyWithETickets(recipientCommandDetails?.eTicketUrls, body, ticketingForm?.id);
    }
    if (recipientCommandDetails?.startUtc) {
        body = replaceBodyVariables(body, DATE_KEY, formatDateTimeToLocaleString(recipientCommandDetails?.startUtc, locale, DateTime.DATETIME_FULL, ticketingForm?.timeZone));
    }
    if (recipientCommandDetails?.endUtc) {
        body = replaceBodyVariables(body, END_DATE_KEY, formatDateTimeToLocaleString(recipientCommandDetails?.endUtc, locale, DateTime.DATETIME_FULL, ticketingForm?.timeZone));
    }
    return body;
};
export const getUnlayerFormLinkVariables = (body) => {
    const unlayerButtonRegex = /{{unlayer-form-link-button-(DonationForm|Ticketing)-([a-zA-Z0-9-]+)(\?[^}]*)?}}/g;
    const variables = [];
    let match;
    while ((match = unlayerButtonRegex.exec(body)) !== null) {
        const [, formType, formId, queryParams] = match;
        variables.push({
            formId,
            formType: formType,
            queryParams: queryParams?.substring(1), // Remove the '?' prefix
        });
    }
    return variables;
};
/**
 * Adds or updates a query parameter in an unlayer form link variable string
 * @param variable The unlayer form link variable string (e.g., "{{unlayer-form-link-button-DonationForm-123?existing=param}}")
 * @param paramName The query parameter name to add/update
 * @param paramValue The query parameter value
 * @returns The updated variable string
 */
export const addQueryParamToUnlayerFormLinkVariable = (variable, paramName, paramValue) => {
    // Extract the base pattern and existing query params
    const match = variable.match(/^({{unlayer-form-link-button-[^}]+?)(\?[^}]*)?}}$/);
    if (!match) {
        return variable; // Not a valid unlayer form link variable
    }
    const [, basePattern, existingParams] = match;
    if (!existingParams) {
        // No existing query params, add new one
        return `${basePattern}?${paramName}=${paramValue}}}`;
    }
    // Parse existing query params
    const queryString = existingParams.substring(1); // Remove the '?'
    const params = new URLSearchParams(queryString);
    // Add or update the parameter
    params.set(paramName, paramValue);
    // Reconstruct the variable
    return `${basePattern}?${params.toString()}}}`;
};
/**
 * Appends or updates a query parameter with a UUID v4 to all links in the body
 * Each link gets a unique UUID
 * Skips any href attributes that contain variables (starting with {{)
 * @param body The email body HTML string
 * @param queryParamName The name of the query parameter to add
 * @returns The updated body with the query parameter added to all links
 */
export const appendUuidToLinks = (body, queryParamName) => {
    // Handle regular href links (excluding any variables starting with {{)
    let updatedBody = body.replace(/href="([^"]+)"/g, (match, url) => {
        // Skip if this is any variable starting with {{
        if (url.startsWith("{{")) {
            return match;
        }
        const linkId = v4(); // Generate unique ID for each link
        try {
            const urlObj = new URL(url);
            urlObj.searchParams.set(queryParamName, linkId);
            return `href="${urlObj.toString()}"`;
        }
        catch {
            // If URL parsing fails (e.g., relative URLs), append manually
            const separator = url.includes("?") ? "&" : "?";
            return `href="${url}${separator}${queryParamName}=${linkId}"`;
        }
    });
    // Handle unlayer form link button variables
    updatedBody = updatedBody.replace(/{{unlayer-form-link-button-[^}]+}}/g, (variable) => {
        const linkId = v4(); // Generate unique ID for each variable
        return addQueryParamToUnlayerFormLinkVariable(variable, queryParamName, linkId);
    });
    return updatedBody;
};
